import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { auth } from "../firebase/config";
import { Shield, Zap, Package, UtensilsCrossed, CheckCircle2, Loader2 } from "lucide-react";

const T = {
  primary:"#f97316", primaryLight:"#fff7ed", primaryMid:"#fed7aa",
  primaryDark:"#ea580c", bg:"#f9f9f9", white:"#ffffff",
  text:"#1c1c1e", textSub:"#6b7280", textMuted:"#9ca3af",
  border:"#e5e7eb", green:"#16a34a", greenLight:"#dcfce7",
  red:"#ef4444",
};

const PERKS = [
  { icon:Zap,             color:T.primary, bg:T.primaryLight, text:"10-minute delivery in Akola"  },
  { icon:UtensilsCrossed, color:"#ea580c", bg:"#ffedd5",      text:"50+ restaurants near you"     },
  { icon:Package,         color:"#2563eb", bg:"#eff6ff",      text:"Real-time order tracking"     },
  { icon:Shield,          color:T.green,   bg:T.greenLight,   text:"100% safe & secure payments"  },
];

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,800;9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  body{background:${T.bg};-webkit-tap-highlight-color:transparent;}
  input,button{font-family:inherit;outline:none;}
  @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes popIn{0%{transform:scale(0.3);opacity:0}65%{transform:scale(1.15)}100%{transform:scale(1);opacity:1}}
  @keyframes ripple{0%{transform:scale(0.85);opacity:0.55}100%{transform:scale(2.5);opacity:0}}
  @keyframes float{0%,100%{transform:translateY(0px)}50%{transform:translateY(-7px)}}
  @keyframes orbMove1{0%,100%{transform:translate(0,0)}40%{transform:translate(35px,-25px)}70%{transform:translate(-18px,18px)}}
  @keyframes orbMove2{0%,100%{transform:translate(0,0)}35%{transform:translate(-28px,22px)}65%{transform:translate(22px,-18px)}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.45}}
  @keyframes slideInLeft{from{opacity:0;transform:translateX(-20px)}to{opacity:1;transform:translateX(0)}}
  .fu1{animation:fadeUp .45s .00s ease both}.fu2{animation:fadeUp .45s .08s ease both}
  .fu3{animation:fadeUp .45s .16s ease both}.fu4{animation:fadeUp .45s .24s ease both}
  .fu5{animation:fadeUp .45s .32s ease both}
  .btn-main{position:relative;overflow:hidden;transition:transform .18s ease,box-shadow .2s ease;}
  .btn-main:active{transform:scale(0.97);}
  .btn-main::after{content:'';position:absolute;inset:0;background:linear-gradient(120deg,rgba(255,255,255,0.22) 0%,transparent 55%);opacity:0;transition:opacity .25s;border-radius:inherit;}
  .btn-main:hover:not(:disabled)::after{opacity:1;}
  .login-card{transition:box-shadow .35s ease;}
  .login-card:hover{box-shadow:0 8px 48px rgba(249,115,22,0.11),0 2px 14px rgba(0,0,0,0.06)!important;}
  ::-webkit-scrollbar{width:4px;}
  ::-webkit-scrollbar-thumb{background:${T.primaryMid};border-radius:4px;}
  .google-btn:hover{transform:translateY(-1px);box-shadow:0 6px 20px rgba(0,0,0,0.12)!important;}
  .google-btn:active{transform:scale(0.98);}
`;

const BASE = typeof window !== "undefined" && window.location.hostname !== "localhost"
  ? "https://quickry-ivrv.onrender.com/api"
  : "http://localhost:5000/api";

function BgOrbs() {
  return (
    <div style={{ position:"fixed",inset:0,overflow:"hidden",pointerEvents:"none",zIndex:0 }}>
      <div style={{ position:"absolute",width:500,height:500,borderRadius:"50%",background:"radial-gradient(circle,rgba(249,115,22,0.07) 0%,transparent 68%)",top:-140,left:-100,animation:"orbMove1 14s ease-in-out infinite" }}/>
      <div style={{ position:"absolute",width:380,height:380,borderRadius:"50%",background:"radial-gradient(circle,rgba(234,88,12,0.05) 0%,transparent 68%)",bottom:-100,right:-80,animation:"orbMove2 17s ease-in-out infinite" }}/>
    </div>
  );
}

function SuccessScreen({ name, isNew, onContinue }) {
  useEffect(() => { const t=setTimeout(onContinue,2300); return ()=>clearTimeout(t); }, []);
  return (
    <div style={{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"60vh",padding:"32px 24px",textAlign:"center" }}>
      <div style={{ position:"relative",marginBottom:26 }}>
        {[0,1,2].map(i=><div key={i} style={{ position:"absolute",inset:-(i+1)*13,borderRadius:"50%",border:`1.5px solid rgba(22,163,74,${0.38-i*0.1})`,animation:`ripple ${1.1+i*0.35}s ease-out infinite`,animationDelay:`${i*0.28}s` }}/>)}
        <div style={{ width:90,height:90,borderRadius:"50%",background:T.greenLight,border:`2.5px solid ${T.green}`,display:"flex",alignItems:"center",justifyContent:"center",position:"relative",zIndex:1,animation:"popIn 0.55s cubic-bezier(.34,1.56,.64,1) both",boxShadow:"0 8px 32px rgba(22,163,74,0.2)" }}>
          <CheckCircle2 size={48} color={T.green} strokeWidth={2.1}/>
        </div>
      </div>
      <div style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"1.75rem",letterSpacing:"-1px",color:T.text,marginBottom:10,animation:"fadeUp 0.5s 0.3s ease both",opacity:0,animationFillMode:"both" }}>
        {isNew?"Welcome to Quickry! 🎉":`Welcome back${name?", "+name.split(" ")[0]:""}! 👋`}
      </div>
      <div style={{ color:T.textSub,fontSize:"0.87rem",lineHeight:1.7,marginBottom:24,animation:"fadeUp 0.5s 0.45s ease both",opacity:0,animationFillMode:"both" }}>
        {isNew?"Your account is ready. Enjoy free delivery on your first order!":"You have been logged in successfully."}
      </div>
      <div style={{ display:"flex",alignItems:"center",gap:7,color:T.textMuted,fontSize:"0.79rem",animation:"fadeUp 0.5s 0.6s ease both",opacity:0,animationFillMode:"both" }}>
        <Loader2 size={14} color={T.primary} style={{ animation:"spin .8s linear infinite" }}/> Redirecting you now...
      </div>
    </div>
  );
}

function DesktopPanel() {
  return (
    <div style={{ width:410,flexShrink:0,background:`linear-gradient(160deg,${T.primaryDark} 0%,${T.primary} 58%,#fb923c 100%)`,display:"flex",flexDirection:"column",justifyContent:"center",padding:"60px 44px",position:"relative",overflow:"hidden" }}>
      <div style={{ position:"absolute",width:290,height:290,borderRadius:"50%",background:"rgba(255,255,255,0.07)",top:-85,right:-75 }}/>
      <div style={{ position:"relative",zIndex:1 }}>
        <div style={{ animation:"slideInLeft 0.6s 0.0s ease both",opacity:0,animationFillMode:"both" }}>
          <div style={{ display:"inline-flex",alignItems:"center",gap:10,background:"rgba(255,255,255,0.15)",borderRadius:14,padding:"8px 16px",marginBottom:28,border:"1px solid rgba(255,255,255,0.2)" }}>
            <div style={{ width:32,height:32,borderRadius:10,background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",animation:"float 3.5s ease-in-out infinite" }}>
              <Zap size={18} color="#fff" strokeWidth={2.5}/>
            </div>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"1.6rem",color:"#fff",letterSpacing:"-1.2px" }}>Quick<span style={{ color:"#fbbf24" }}>ry</span></div>
          </div>
        </div>
        <div style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"2rem",color:"#fff",lineHeight:1.2,marginBottom:12,letterSpacing:"-0.5px",animation:"slideInLeft 0.6s 0.1s ease both",opacity:0,animationFillMode:"both" }}>Akola's Fastest<br/>Delivery App</div>
        <div style={{ color:"rgba(255,255,255,0.82)",fontSize:"0.87rem",lineHeight:1.75,marginBottom:42,animation:"slideInLeft 0.6s 0.2s ease both",opacity:0,animationFillMode:"both" }}>Food, groceries, courier & medicines —<br/>delivered right to your doorstep.</div>
        {PERKS.map((p,i) => { const Icon=p.icon; return (
          <div key={i} style={{ display:"flex",alignItems:"center",gap:14,marginBottom:18,animation:`slideInLeft 0.5s ${0.3+i*0.1}s ease both`,opacity:0,animationFillMode:"both" }}>
            <div style={{ width:42,height:42,borderRadius:13,background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
              <Icon size={18} color="#fff" strokeWidth={1.8}/>
            </div>
            <span style={{ color:"rgba(255,255,255,0.92)",fontSize:"0.87rem",fontWeight:500 }}>{p.text}</span>
          </div>
        );})}
      </div>
    </div>
  );
}

function Outer({ children, isDesk, isMobile }) {
  return (
    <div style={{ minHeight:"100vh",background:T.bg,fontFamily:"'Plus Jakarta Sans',sans-serif",color:T.text,display:isDesk?"flex":"block",position:"relative" }}>
      <style>{GLOBAL_STYLES}</style>
      <BgOrbs/>
      {isDesk && <DesktopPanel/>}
      <div style={{ flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:isMobile?"0 0 40px":"40px 24px",minHeight:"100vh",position:"relative",zIndex:1 }}>
        {!isDesk && (
          <div style={{ background:`linear-gradient(135deg,${T.primaryDark},${T.primary})`,borderRadius:"0 0 28px 28px",padding:"36px 24px 28px",marginBottom:24,textAlign:"center",position:"relative",overflow:"hidden",width:"100%",animation:"fadeIn 0.5s ease both" }}>
            <div style={{ position:"absolute",width:190,height:190,borderRadius:"50%",background:"rgba(255,255,255,0.07)",top:-55,right:-35 }}/>
            <div style={{ position:"relative",zIndex:1 }}>
              <div style={{ display:"inline-flex",alignItems:"center",gap:8,marginBottom:6 }}>
                <div style={{ width:34,height:34,borderRadius:10,background:"rgba(255,255,255,0.18)",display:"flex",alignItems:"center",justifyContent:"center",animation:"float 3s ease-in-out infinite" }}>
                  <Zap size={18} color="#fff" strokeWidth={2.5}/>
                </div>
                <div style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"2rem",color:"#fff",letterSpacing:"-1.5px" }}>Quick<span style={{ color:"#fbbf24" }}>ry</span></div>
              </div>
              <div style={{ color:"rgba(255,255,255,0.82)",fontSize:"0.8rem",display:"flex",alignItems:"center",justifyContent:"center",gap:6 }}>
                <div style={{ width:6,height:6,borderRadius:"50%",background:"#4ade80",boxShadow:"0 0 6px rgba(74,222,128,0.8)",animation:"pulse 1.8s ease-in-out infinite" }}/>
                Akola's fastest delivery app
              </div>
            </div>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

function GoogleBtn({ onClick, loading }) {
  return (
    <button className="google-btn btn-main" onClick={onClick} disabled={loading}
      style={{ width:"100%",background:"#fff",border:`1.5px solid ${T.border}`,borderRadius:13,padding:"14px",display:"flex",alignItems:"center",justifyContent:"center",gap:10,cursor:loading?"not-allowed":"pointer",transition:"all 0.2s",boxShadow:"0 2px 8px rgba(0,0,0,0.07)",fontFamily:"inherit" }}>
      {loading
        ? <Loader2 size={18} color="#666" style={{ animation:"spin .75s linear infinite" }}/>
        : <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
      }
      <span style={{ fontWeight:700,fontSize:"0.95rem",color:"#3c4043" }}>
        {loading?"Signing in...":"Continue with Google"}
      </span>
    </button>
  );
}

export default function LoginPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { login } = useAuth();
  const from = location.state?.from || "/";

  const [screen,        setScreen]        = useState("input");
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error,         setError]         = useState("");
  const [isNew,         setIsNew]         = useState(false);
  const [loggedInName,  setLoggedInName]  = useState("");
  const [width,         setWidth]         = useState(window.innerWidth);

  useEffect(() => { const h=()=>setWidth(window.innerWidth); window.addEventListener("resize",h); return ()=>window.removeEventListener("resize",h); }, []);

  const isMobile = width < 768;
  const isDesk   = width >= 1024;

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError("");
    try {
      const provider = new GoogleAuthProvider();
      const result   = await signInWithPopup(auth, provider);
      const idToken  = await result.user.getIdToken();

      const res  = await fetch(`${BASE}/auth/google`, {
        method:  "POST",
        headers: { "Content-Type":"application/json" },
        body:    JSON.stringify({ idToken }),
      });
      const data = await res.json();

      if (data.success) {
        login(data.data || data.user, data.token);
        setLoggedInName(data.data?.name || result.user.displayName || "");
        setIsNew(data.isNewUser || false);
        setScreen("success");
      } else {
        setError(data.message || "Google login failed. Please try again.");
      }
    } catch (e) {
      console.error("Google login error:", e);
      setError("Google login failed. Please try again.");
    }
    setGoogleLoading(false);
  };

  // ─── SCREEN: INPUT ─────────────────────────────
  if (screen === "input") return (
    <Outer isDesk={isDesk} isMobile={isMobile}>
      <div style={{ width:"100%",maxWidth:400,padding:isMobile?"0 16px":"0" }}>
        <div className="fu1 login-card" style={{ background:T.white,borderRadius:22,border:`1px solid ${T.border}`,padding:isMobile?"28px 20px":"36px 32px",boxShadow:"0 4px 28px rgba(0,0,0,0.07)" }}>

          <div className="fu1" style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"1.7rem",letterSpacing:"-0.8px",marginBottom:6,color:T.text }}>Welcome! 👋</div>
          <div className="fu2" style={{ color:T.textSub,fontSize:"0.85rem",marginBottom:28,lineHeight:1.65 }}>Sign in to continue to Quickry.</div>

          <div className="fu3" style={{ marginBottom:error?16:0 }}>
            <GoogleBtn onClick={handleGoogleLogin} loading={googleLoading}/>
          </div>

          {error && (
            <div style={{ marginTop:14,color:T.red,fontSize:"0.78rem",fontWeight:600,textAlign:"center",background:"#fef2f2",border:"1px solid rgba(239,68,68,0.2)",borderRadius:10,padding:"10px 14px" }}>
              {error}
            </div>
          )}

          <div className="fu5" style={{ fontSize:"0.7rem",color:T.textMuted,textAlign:"center",lineHeight:1.6,marginTop:22 }}>
            By continuing, you agree to our <span style={{ color:T.primary,fontWeight:600,cursor:"pointer" }}>Terms of Service</span> & <span style={{ color:T.primary,fontWeight:600,cursor:"pointer" }}>Privacy Policy</span>
          </div>
        </div>
      </div>
    </Outer>
  );

  // ─── SCREEN: SUCCESS ───────────────────────────
  if (screen === "success") return (
    <Outer isDesk={isDesk} isMobile={isMobile}>
      <div style={{ width:"100%",maxWidth:400,padding:isMobile?"0 16px":"0" }}>
        <div style={{ background:T.white,borderRadius:22,border:`1px solid ${T.border}`,boxShadow:"0 4px 28px rgba(0,0,0,0.07)",overflow:"hidden" }}>
          <SuccessScreen name={loggedInName} isNew={isNew} onContinue={()=>navigate(from,{ replace:true })}/>
        </div>
      </div>
    </Outer>
  );

  return null;
}